import React, { FC } from "react";
import {
  Controller,
  Control,
  UseFormRegister,
  UseFormSetValue,
  UseFormSetError,
  UseFormClearErrors,
  UseFieldArrayRemove,
} from "react-hook-form";
import RemoveItem from "app/components/common/buttons/RemoveItem";
import InputField from "app/components/common/inputs/InputField";
import FormGroup from "app/components/common/inputs/FormGroup";
import AutocompleteLocations from "app/components/common/AutocompleteLocations";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import {
  holdersOptions,
  RemainingloanTermOptions,
} from "app/utils/staticValues";
import SelectField from "app/components/common/inputs/SelectField";
import RadioButton from "app/components/common/buttons/RadioButton";

interface FieldItem {
  id: string;
  dbId?: string;
}
interface Props {
  control: Control<any>;
  errors: any;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  setError: UseFormSetError<any>;
  clearErrors: UseFormClearErrors<any>;
  fields: FieldItem[];
  deleteItem: any;
  existingMortgage: string;
  remove: UseFieldArrayRemove;
  applicantID: string;
  deleteIsPending?: boolean;
}

const DynamicExistingMortgage: FC<Props> = ({
  control,
  errors,
  register,
  setValue,
  setError,
  clearErrors,
  fields,
  existingMortgage,
  deleteItem,
  applicantID,
  remove,
  deleteIsPending = false,
}) => {
  const getErrorMessage = (fieldName: string) => {
    const fieldNames = fieldName.split(".");
    return fieldNames.reduce((acc, key) => acc && acc[key], errors);
  };
  return (
    <>
      {fields.map((field, index) => {
        const basePath = `${existingMortgage}.${index}`;
        return (
          <>
            <React.Fragment key={field.id}>
              <FormGroup className="relative">
                <InputField
                  label={`lender name`}
                  name={`${basePath}.lenderName`}
                  control={control}
                  type="text"
                  required={true}
                  placeholder="Annette Black"
                  error={
                    getErrorMessage(`${basePath}.lenderName`)?.message as string
                  }
                  className="flex-1"
                />
                <InputField
                  label={`balance`}
                  name={`${basePath}.balance`}
                  control={control}
                  type="number"
                  required={true}
                  placeholder="$490.51"
                  error={
                    getErrorMessage(`${basePath}.balance`)?.message as string
                  }
                  className="flex-1"
                  rules={{
                    min: {
                      value: 1,
                      message: "value must be greater than 0",
                    },
                  }}
                />
                <InputField
                  label={`Available redraw`}
                  name={`${basePath}.availableRedraw`}
                  control={control}
                  type="number"
                  required={true}
                  placeholder="$8888"
                  error={
                    getErrorMessage(`${basePath}.availableRedraw`)
                      ?.message as string
                  }
                  className="flex-1"
                  rules={
                    {
                      // min: {
                      //   value: 1,
                      //   message: "value must be greater than 0",
                      // },
                    }
                  }
                />{" "}
                <InputField
                  label={`Min. monthly payment`}
                  name={`${basePath}.minMonthlyPayment`}
                  control={control}
                  type="number"
                  required={true}
                  placeholder="$446.61"
                  error={
                    getErrorMessage(`${basePath}.minMonthlyPayment`)
                      ?.message as string
                  }
                  className="flex-1"
                  rules={{
                    min: {
                      value: 1,
                      message: "value must be greater than 0",
                    },
                  }}
                />
                {index > 0 && (
                  <RemoveItem
                    className="absolute right-0"
                    disabled={deleteIsPending}
                    onClick={() => {
                      if (field?.dbId) {
                        deleteItem(
                          `/asset-liability/${applicantID}/existingmortgage/${field?.dbId}`,
                          {
                            onSuccess: () => {
                              remove(index);
                            },
                          }
                        );
                      } else {
                        remove(index);
                      }
                    }}
                  />
                )}
              </FormGroup>
              <FormGroup className="relative">
                <SelectField
                  label="Remaining loan term"
                  name={`${basePath}.loanTerms`}
                  control={control}
                  options={RemainingloanTermOptions}
                  placeholder="2"
                  error={
                    getErrorMessage(`${basePath}.loanTerms`)?.message as string
                  }
                  required={true}
                  className="flex-1"
                />
                <InputField
                  label={`Interest rate`}
                  name={`${basePath}.interestrate`}
                  control={control}
                  type="text"
                  required={true}
                  placeholder="2.5%"
                  error={
                    getErrorMessage(`${basePath}.interestrate`)
                      ?.message as string
                  }
                  className="flex-1"
                  rules={{
                    required: "Interest rate is required",
                    min: {
                      value: 0,
                      message: "Interest rate must be at least 0%",
                    },
                    max: {
                      value: 100,
                      message: "Interest rate cannot be more than 100%",
                    },
                  }}
                />
                <SelectField
                  label="No. of title holders"
                  name={`${basePath}.noOfTitleHolders`}
                  control={control}
                  options={holdersOptions}
                  placeholder="2"
                  error={
                    getErrorMessage(`${basePath}.noOfTitleHolders`)
                      ?.message as string
                  }
                  required={true}
                  className="flex-1"
                />

                <AutocompleteLocations
                  FieldName={`${basePath}.propertyAddress`}
                  placeholder="2464 Royal Ln. Mesa..."
                  register={register}
                  setValue={setValue}
                  setError={setError}
                  label="Property address"
                  required={true}
                  className="flex-1"
                  error={
                    getErrorMessage(`${basePath}.propertyAddress`)
                      ?.message as string
                  }
                  // stateFieldName="employerAddress.state"
                  // postCodeFieldName="employerAddress.postCode"
                  clearErrors={clearErrors}
                />
              </FormGroup>
              <FormGroup className="relative">
                <div className="text-left ">
                  <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                    Is it a investment loan?
                  </h6>
                  <div className="flex gap-5">
                    <Controller
                      name={`${basePath}.isInvestmentLoan`}
                      control={control}
                      render={({ field }) => (
                        <div className="flex gap-5">
                          <RadioButton
                            id={`${basePath}_investment_yes_${index}`}
                            label="Yes"
                            value={true}
                            checked={field.value === true}
                            onChange={() => field.onChange(true)}
                          />
                          <RadioButton
                            id={`${basePath}_investment_no_${index}`}
                            label="No"
                            value={false}
                            checked={field.value === false}
                            onChange={() => field.onChange(false)}
                          />
                        </div>
                      )}
                    />
                  </div>
                  <ErrorsMessage
                    title={
                      getErrorMessage(`${basePath}.isInvestmentLoan`)
                        ?.message as string
                    }
                    className="!text-left"
                  />
                </div>
                <div className="text-left">
                  <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                    To be refinanced?
                  </h6>
                  <div className="flex gap-5">
                    <Controller
                      name={`${basePath}.toBeRefinanced`}
                      control={control}
                      render={({ field }) => (
                        <div className="flex gap-5">
                          <RadioButton
                            id={`${basePath}_toBeRefinanced_yes_${index}`}
                            label="Yes"
                            value={true}
                            checked={field.value === true}
                            onChange={() => field.onChange(true)}
                          />
                          <RadioButton
                            id={`${basePath}_toBeRefinanced_no_${index}`}
                            label="No"
                            value={false}
                            checked={field.value === false}
                            onChange={() => field.onChange(false)}
                          />
                        </div>
                      )}
                    />
                  </div>
                  <ErrorsMessage
                    title={
                      getErrorMessage(`${basePath}.toBeRefinanced`)
                        ?.message as string
                    }
                    className="!text-left"
                  />
                </div>
              </FormGroup>
            </React.Fragment>
          </>
        );
      })}
    </>
  );
};

export default DynamicExistingMortgage;
